import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import { AerosMultiAutocomplete, type AerosMultiAutocompleteProps } from "../AerosMultiAutocomplete"

interface FormMultiAutocompleteProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
> extends Omit<AerosMultiAutocompleteProps<TValue>, "name" | "defaultValue" | "value" | "onChange"> {
  control: Control<TFieldValues>
  name: TName
  freeSolo?: boolean
  onChange?: (value: TValue[]) => void
}

export const FormMultiAutocomplete = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
>({
  control,
  name,
  helperText,
  onChange,
  freeSolo = false,
  ...props
}: FormMultiAutocompleteProps<TFieldValues, TName, TValue>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (value: TValue[]) => {
    field.onChange(value)
    onChange?.(value)
  }

  return (
    <AerosMultiAutocomplete<TValue>
      {...field}
      {...props}
      freeSolo={freeSolo}
      error={!!error}
      helperText={error?.message ?? helperText}
      onChange={handleChange}
    />
  )
}
